import React from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Languages, 
  Sun, 
  Moon, 
  Lock, 
  FlipHorizontal2, 
  LogOut, 
  Pin, 
  PinOff,
  RotateCcw,
  MoreVertical
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuPortal,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { useScriptLimits } from '@/hooks/useScriptLimits';
import { AudioWaveform } from '@/components/recording/AudioWaveform';

interface TopControlsProps {
  elapsedTime: number;
  totalTime: number;
  showTimer: boolean;
  theme: "light" | "dark";
  onToggleTheme: () => void;
  mirrorMode: boolean;
  onToggleMirror: () => void;
  onExit: () => void;
  isPinned?: boolean;
  onTogglePin?: () => void;
  onReset: () => void;
  stream?: MediaStream | null;
  isRecording?: boolean;
}



function formatTime(seconds: number): string {
  if (!isValidNumber(seconds)) return "00:00";
  const absSeconds = Math.abs(seconds);
  const h = Math.floor(absSeconds / 3600);
  const m = Math.floor((absSeconds % 3600) / 60);
  const s = Math.floor(absSeconds % 60);
  return `${h > 0 ? h + ':' : ''}${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
}

function isValidNumber(num: number): boolean {
  return typeof num === 'number' && !isNaN(num) && isFinite(num);
}


export function TopControls({
  elapsedTime,
  totalTime,
  showTimer,
  theme,
  onToggleTheme,
  mirrorMode,
  onToggleMirror,
  onExit,
  isPinned,
  onTogglePin,
  onReset,
  stream,
  isRecording
}: TopControlsProps) {
  const { t, i18n } = useTranslation();
  const { canUseMirrorMode } = useScriptLimits();
  const mirrorAllowed = canUseMirrorMode();

  return (
    <>
      {/* ================= DESKTOP VIEW (Strictly Preserved) ================= */}
      <div className="hidden md:block pointer-events-none absolute inset-0 z-50">
          {/* Top Left: Timer & Live Indicator */}
          {showTimer && (
            <div className="absolute top-6 left-6 pointer-events-auto">
              <div className="flex items-center gap-3 bg-zinc-900 h-10 px-4 rounded-xl border-2 border-white shadow-[2px_2px_0_white]">
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${isRecording ? 'bg-red-500 animate-pulse' : 'bg-red-500/50'}`} />
                  <span className={`font-bold text-xs tracking-wider ${isRecording ? 'text-red-500' : 'text-red-500/50'}`}>LIVE</span>
                </div>
                <div className="w-px h-4 bg-white/20" />
                <span className="font-mono text-xl font-bold text-white tabular-nums">
                  {formatTime(elapsedTime)}
                </span>
                {/* Waveform for Desktop */}
                <div className="w-px h-4 bg-white/20" />
                <AudioWaveform 
                  stream={stream || null}
                  isRecording={!!isRecording}
                  width={80} 
                  height={20}
                  barColor="#6FEDD6"
                />
              </div>
            </div>
          )}

          {/* Top Right: Action Buttons Group */}
          <div className="absolute top-6 right-6 flex flex-col gap-4 items-end pointer-events-auto">
            <div className="flex items-center gap-2 bg-zinc-900 px-2 py-0.5 rounded-2xl border-2 border-white shadow-[2px_2px_0_white]">
                
                {/* Theme Toggle */}
                <Button variant="ghost" size="icon" onClick={onToggleTheme} className="h-[30px] w-[30px] text-zinc-400 hover:text-white hover:bg-zinc-700 rounded-lg">
                    {theme === 'dark' ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
                </Button>

                {/* Language Selector */}
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-[30px] w-[30px] text-zinc-300 hover:text-white hover:bg-white/10 rounded-xl">
                        <Languages className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-800 text-zinc-300 z-[60]">
                      <DropdownMenuItem className="focus:bg-zinc-800 focus:text-white cursor-pointer" onClick={() => i18n.changeLanguage("en")}>English</DropdownMenuItem>
                      <DropdownMenuItem className="focus:bg-zinc-800 focus:text-white cursor-pointer" onClick={() => i18n.changeLanguage("zh-TW")}>繁體中文</DropdownMenuItem>
                      <DropdownMenuItem className="focus:bg-zinc-800 focus:text-white cursor-pointer" onClick={() => i18n.changeLanguage("zh-CN")}>简体中文</DropdownMenuItem>
                      <DropdownMenuItem className="focus:bg-zinc-800 focus:text-white cursor-pointer" onClick={() => i18n.changeLanguage("ja")}>日本語</DropdownMenuItem>
                      <DropdownMenuItem className="focus:bg-zinc-800 focus:text-white cursor-pointer" onClick={() => i18n.changeLanguage("ko")}>한국어</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>

                  {/* Refresh / Reload Button */}
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={onReset} 
                    className="h-[30px] w-[30px] text-zinc-400 hover:text-white hover:bg-zinc-700 rounded-lg"
                    title={t("common.refresh", "Refresh")}
                  >
                    <RotateCcw className="h-4 w-4" />
                  </Button>

                  {/* Mirror Mode */}
                  <Button 
                      variant="ghost" size="icon"
                      onClick={mirrorAllowed ? onToggleMirror : undefined}
                      className={cn(
                        "h-9 w-9 rounded-lg transition-all",
                        mirrorMode ? "bg-[#FF6B9D] text-black border-2 border-white" : "text-zinc-400 hover:text-white hover:bg-zinc-700",
                        !mirrorAllowed && "opacity-50 cursor-not-allowed"
                      )}
                  >
                    {!mirrorAllowed ? <Lock className="h-4 w-4" /> : <FlipHorizontal2 className="h-4 w-4" />}
                  </Button>
                  
                  {/* Exit Button */}
                  <div className="w-px h-4 bg-white/20 mx-1" />
                  
                  <Button 
                      onClick={onExit} 
                      className="h-[30px] px-3 rounded-lg bg-red-500 text-white border-2 border-white hover:bg-red-600 transition-all flex items-center gap-2"
                      size="sm"
                  >
                      <span className="text-xs font-bold uppercase tracking-wider">{t("common.exit", "退出")}</span>
                      <LogOut className="h-3.5 w-3.5" />
                  </Button>
            </div>
          </div>
      </div>

      {/* ================= MOBILE VIEW (Redesigned) ================= */}
      <div className="md:hidden absolute top-0 left-0 right-0 z-50 pointer-events-auto pt-safe-top">
         <div className="flex items-start justify-between px-4 pt-8 pb-3 bg-gradient-to-b from-black/80 to-transparent">
            {/* Left: Timer & Waveform */}
            {showTimer && (
              <div className="flex flex-col gap-1 items-start">
                  <div className="flex items-center gap-2 bg-black/40 backdrop-blur-md px-3 py-1.5 rounded-lg border border-white/10">
                     <div className={`w-1.5 h-1.5 rounded-full ${isRecording ? 'bg-red-500 animate-pulse' : 'bg-red-500/50'}`} />
                     <span className="font-mono text-lg font-bold text-white leading-none">
                        {formatTime(elapsedTime)}
                     </span>
                  </div>
                  {/* Waveform aligned below */}
                  <div className="px-1 overflow-hidden opacity-80 mt-3">
                      <AudioWaveform 
                          stream={stream || null}
                          isRecording={!!isRecording}
                          width={80} 
                          height={16}
                          barColor="#6FEDD6"
                      />
                  </div>
              </div>
            )}

            {/* Right: Actions */}
            <div className="flex items-center gap-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-9 w-9 text-white/80 hover:text-white hover:bg-white/10 rounded-lg">
                      <MoreVertical className="h-5 w-5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-black/95 backdrop-blur-xl border-zinc-800 p-1.5 min-w-0 w-auto flex flex-col gap-1 rounded-2xl z-[60] shadow-2xl">
                    {/* Theme Toggle */}
                    <DropdownMenuItem 
                      onClick={onToggleTheme}
                      className="flex justify-center items-center h-10 w-10 p-0 rounded-xl focus:bg-zinc-800 cursor-pointer outline-none transition-colors"
                    >
                      {theme === 'dark' ? <Moon className="h-5 w-5 text-zinc-300" /> : <Sun className="h-5 w-5 text-zinc-300" />}
                    </DropdownMenuItem>

                    {/* Language Selector */}
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger className="flex justify-center items-center h-10 w-10 p-0 rounded-xl focus:bg-zinc-800 cursor-pointer data-[state=open]:bg-zinc-800 outline-none transition-colors [&>svg:last-child]:hidden">
                        <Languages className="h-5 w-5 text-zinc-300" />
                      </DropdownMenuSubTrigger>
                      <DropdownMenuPortal>
                        <DropdownMenuSubContent className="bg-zinc-950 border-zinc-800 text-zinc-300 min-w-[8rem] z-[60] rounded-xl p-1 shadow-xl">
                          <DropdownMenuItem className="focus:bg-zinc-800 focus:text-white cursor-pointer rounded-lg" onClick={() => i18n.changeLanguage("en")}>English</DropdownMenuItem>
                          <DropdownMenuItem className="focus:bg-zinc-800 focus:text-white cursor-pointer rounded-lg" onClick={() => i18n.changeLanguage("zh-TW")}>繁體中文</DropdownMenuItem>
                          <DropdownMenuItem className="focus:bg-zinc-800 focus:text-white cursor-pointer rounded-lg" onClick={() => i18n.changeLanguage("zh-CN")}>简体中文</DropdownMenuItem>
                          <DropdownMenuItem className="focus:bg-zinc-800 focus:text-white cursor-pointer rounded-lg" onClick={() => i18n.changeLanguage("ja")}>日本語</DropdownMenuItem>
                          <DropdownMenuItem className="focus:bg-zinc-800 focus:text-white cursor-pointer rounded-lg" onClick={() => i18n.changeLanguage("ko")}>한국어</DropdownMenuItem>
                        </DropdownMenuSubContent>
                      </DropdownMenuPortal>
                    </DropdownMenuSub>

                    {/* Reset Button */}
                    <DropdownMenuItem 
                      onClick={onReset}
                      className="flex justify-center items-center h-10 w-10 p-0 rounded-xl focus:bg-zinc-800 cursor-pointer outline-none transition-colors"
                    >
                       <RotateCcw className="h-5 w-5 text-zinc-300" />
                    </DropdownMenuItem>
                    
                    {/* Mirror Mode */}
                    <DropdownMenuItem 
                      onClick={mirrorAllowed ? onToggleMirror : undefined}
                      disabled={!mirrorAllowed}
                      className={cn(
                        "flex justify-center items-center h-10 w-10 p-0 rounded-xl focus:bg-zinc-800 cursor-pointer outline-none transition-colors",
                        mirrorMode ? "bg-pink-500/20 text-pink-400 focus:bg-pink-500/30" : "text-zinc-300",
                        !mirrorAllowed && "opacity-50 cursor-not-allowed"
                      )}
                    >
                       {!mirrorAllowed ? <Lock className="h-5 w-5" /> : <FlipHorizontal2 className="h-5 w-5" />}
                    </DropdownMenuItem>

                    {/* Exit */}
                    <DropdownMenuItem 
                       onClick={onExit}
                       className="flex justify-center items-center h-10 w-10 p-0 rounded-xl focus:bg-red-500/20 active:bg-red-500/30 cursor-pointer outline-none transition-colors"
                    >
                       <LogOut className="h-5 w-5 text-red-500" />
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
            </div>
         </div>
      </div>
    </>
  );
}
